import { useGetAllSettings, useGetOrganizations } from "services/ContentManager";
import { isAdmin as getIsAdmin } from "services/PermissionManager";
import { 
    Typography,
    Grid,
    ListItem, 
    Autocomplete, 
    Checkbox, 
    TextField, 
    CardActionArea,
    Box 
} from "@mui/material";
import { FiberManualRecord } from '@mui/icons-material'
import React from "react";
import { UserContext } from "containers/App";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
import { TextInputContainer } from "components/forms/TextInput";
import BaseModal from "components/modals/BaseModal";
import { makeStyles } from "@mui/styles";

dayjs.extend(isBetween);

const BOOLEAN_ICONS = {
    true: <FiberManualRecord style={{color: "#43ff00"}}/>,
    false: <FiberManualRecord style={{color: "#ff0000"}}/>
};

const useStyles = makeStyles((theme) => ({
    modalGrid: {
        margin: 0,
        width: '100%',
        marginBottom: '1.5rem',
    },
    boxContainer: {
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
    },
    boxOnlyActiveOrgFlag: {
        width: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '24px',
        transition: '0.2s',
        boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: theme.palette.green.main,
        backgroundColor: theme.palette.primary.light,
    },
    boxOnlyActiveOrgFlagSelected: {
        width: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '24px',
        boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)',
        transition: '0.2s',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: theme.palette.primary.light,
        backgroundColor: theme.palette.green.main,
    },
    autocompleteContainer: {
        maxWidth: '50%',
        borderRadius: '4px',
    },
    listItemOption: {
        '&[aria-selected="true"]': {
            backgroundColor: theme.palette.primary.light,
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    typographyOption: {
        flexGrow: 1,
    },
}));

export default function StartNotificationJobModal({ open, onClose, onSave }) {

    const classes = useStyles();
    const isAdmin = getIsAdmin(React.useContext(UserContext));
    const organizationsAndActiveStatus = useOrganizationsAndActiveStatus(isAdmin);
    const [messageValues, setMessageValues] = React.useState({});
    const [error, setError] = React.useState({});
    const [onlyActive, setOnlyActive] = React.useState(false);
    const { t } = useTranslation();

    const filteredOrganizations = React.useMemo(() => {
        if (!organizationsAndActiveStatus) return [];

        return organizationsAndActiveStatus
            .filter(org => onlyActive ? org.isActive : true)
            .sort((org1, org2) => org1.title.localeCompare(org2.title));
    }, [organizationsAndActiveStatus, onlyActive]);


    function validateFields(values) {
        const newErrors = {};
        let hasErrors = false;
        
        if(!values.title) {
            hasErrors = true;
            newErrors.title = t('mandatoryField');
        }
        if(!values.body) {
            hasErrors = true;
            newErrors.body = t('mandatoryField');
        }
        if(!values.organizations || values.organizations.length === 0) {
            hasErrors = true;
            newErrors.organizations = t('mandatoryField');
        }
        
        setError(newErrors);
        return hasErrors;
    }

    const onTextChange = (value, name) => {
        setError(prev => ({...prev, [name]: undefined}));
        setMessageValues(prev => ({...prev, [name]: value}));
    };

    const handleSave = () => {
        if (!validateFields(messageValues)) {
            onSave(messageValues);
        }
    };

    return (
        <BaseModal open={open} onClose={onClose} onSave={handleSave} fullWidth>
            <Grid container spacing={4} className={classes.modalGrid}>
                <TextInputContainer
                    xs={12}
                    label={t('title')}
                    value={messageValues.title || ""}
                    type="string"
                    error={error.title}
                    onTextChange={(value) => onTextChange(value, "title")}
                />
                <TextInputContainer
                    xs={12}
                    label={t('body')}
                    value={messageValues.body || ""}
                    error={error.body}
                    type="string"
                    multiline={true}
                    onTextChange={(value) => onTextChange(value, "body")}
                />
                <Box className={classes.boxContainer}>
                    <Box
                        variant="outlined"
                        id="box-select-only-active-orgs"
                        className={onlyActive
                            ? classes.boxOnlyActiveOrgFlagSelected
                            : classes.boxOnlyActiveOrgFlag
                        }
                    >
                        <CardActionArea 
                            sx={{ borderRadius: '24px' }} 
                            onClick={() => setOnlyActive(!onlyActive)}
                        >
                            <Typography sx={{ fontWeight: 500, p: 1 }}>
                                {t('showOnlyActiveOrganizations')}
                            </Typography>
                        </CardActionArea>
                    </Box>
                    <Autocomplete
                        multiple
                        fullWidth
                        disableCloseOnSelect
                        className={classes.autocompleteContainer}
                        options={filteredOrganizations}
                        getOptionLabel={(option) => option.title}
                        value={messageValues.organizations || []}
                        onChange={(_, newValue) => {
                            setError(prev => ({...prev, organizations: undefined}));
                            setMessageValues(prev => ({
                                ...prev,
                                organizations: newValue.map(o => ({ id: o.id, title: o.title })),
                            }));
                        }}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('initiatives')}
                                error={!!error.organizations}
                                helperText={error.organizations}
                                placeholder={t('selectOrganization')}
                            />
                        )}
                        renderOption={(props, option, { selected }) => {
                            return (
                            <ListItem 
                                {...props}
                                key={option.id}
                                className={classes.listItemOption}
                            >
                                <Checkbox checked={selected} />
                                <Typography variant="body2" className={classes.typographyOption}>
                                    {option.title}
                                </Typography>
                                {BOOLEAN_ICONS[option.isActive]}
                            </ListItem>
                        )}}
                    />
                </Box>
            </Grid>
        </BaseModal>
    );
}

export function useOrganizationsAndActiveStatus(isAdmin) {
    const { organizations } = useGetOrganizations(isAdmin);
    const { settings: allSettings } = useGetAllSettings();
    const currentDateTime = React.useMemo(() => dayjs(), []);

    const dateRangesByOrg = React.useMemo(() => {
        if (!allSettings) return {};
        const ranges = {};
        for (const setting of allSettings) {
            if (!ranges[setting.organization]) {
                ranges[setting.organization] = {};
            } 

            if (['startDateUrbanPoints', 'endDateUrbanPoints'].includes(setting.organizationSetting)) {
                ranges[setting.organization][setting.organizationSetting] = setting.value;
            }
        }
        return ranges;
    }, [allSettings]);

    const orgsWithStatus = React.useMemo(() => {
        if (!organizations) return [];
        return organizations.map(org => {
            const startDateStr = dateRangesByOrg[org.id]?.startDateUrbanPoints;
            const endDateStr = dateRangesByOrg[org.id]?.endDateUrbanPoints;

            const start = startDateStr ? dayjs(startDateStr).startOf('day') : null;
            const end = endDateStr ? dayjs(endDateStr).endOf('day') : null;

            return {
                id: org.id,
                title: org.title,
                isActive: start && end 
                    ? currentDateTime.isBetween(start, end, null, '[]') 
                    : false,
            };
        });
    }, [organizations, dateRangesByOrg, currentDateTime]);

    return orgsWithStatus;
}